import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../widgets/LangContext";
import { useSelector } from "react-redux";
import {
    MoneyFormat,
    NumberToNepaliNum,
} from "../../../../widgets/NumberFormat";
import AppURL from "../../../../widgets/AppURL";
import { NepNowDateFormat } from "../../../../widgets/DateFormat";

const PrintYearlyBudgetReportView = () => {
    const { language } = useLanguage();
    const { menu_list } = useSelector((state) => state?.software);

    const Org_Details = menu_list?.structure;
    const DateNow = NepNowDateFormat();

    const { account_trial_balance_rpt_list: data, account_rpt_from_to_date_list } = useSelector((state) => state.accounts);
    const [totalEstimates, setTotalEstimates] = useState({ income: 0, expense: 0 });
    const [totalActuals, setTotalActuals] = useState({ income: 0, expense: 0 });
    const [totalDifferences, setTotalDifferences] = useState({ income: 0, expense: 0 });

    // Separate data into income and expense categories
    const { incomeItems, expenseItems } = useMemo(() => {
        const income = [];
        const expense = [];

        data?.results?.forEach(item => {
            if (item.glid === 150 || item.glid === 130) {
                expense.push(item);
            } else {
                income.push(item);
            }
        });

        return { incomeItems: income, expenseItems: expense };
    }, [data]);

    // Calculate totals
    useEffect(() => {
        if (!data?.results) return;

        let incomeEstimate = 0, incomeActual = 0, incomeDiff = 0;
        let expenseEstimate = 0, expenseActual = 0, expenseDiff = 0;

        data.results.forEach(item => {
            if (item.glid === 150 || item.glid === 130) {
                expenseEstimate += item.estimated_amount || 0;
                expenseActual += item.real_amount || 0;
                expenseDiff += item.difference_amount || 0;
            } else {
                incomeEstimate += item.estimated_amount || 0;
                incomeActual += item.real_amount || 0;
                incomeDiff += item.difference_amount || 0;
            }
        });

        setTotalEstimates({ income: incomeEstimate, expense: expenseEstimate });
        setTotalActuals({ income: incomeActual, expense: expenseActual });
        setTotalDifferences({ income: incomeDiff, expense: expenseDiff });
    }, [data]);

    // Helper function to render table rows
    const renderTableRows = (items, isIncome) => {
        const maxRows = Math.max(incomeItems.length, expenseItems.length);

        return Array.from({ length: maxRows }).map((_, index) => {
            const item = items[index];

            if (!item) {
                // Empty row for alignment
                return (
                    <tr key={`${isIncome ? 'income' : 'expense'}-empty-${index}`}
                        className="bg-white dark:bg-slate800 border-gray-300">
                        <td align='center' className="px-3 py-1 border border-gray-300"></td>
                        <td align='center' className="px-3 py-1 border border-gray-300"></td>
                        <td align='right' className="px-3 py-1 border border-gray-300"></td>
                        <td align='right' className="px-3 py-1 border border-gray-300"></td>
                        <td align='right' className="px-3 py-1 border border-gray-300"></td>
                    </tr>
                );
            }

            const diffClass = item.difference_amount >= 0
                ? 'text-gray-600'
                : 'text-gray-600 ';

            return (
                <tr key={item.sglsn}
                    className="bg-white font-public-sans dark:bg-slate800 border-b dark:border-slate700 hover:bg-gray-50 dark:hover:bg-slate-700">
                    <td align='center' className={`${language ? "font-public-sans" : "font-sangrila"} px-1 w-10 py-1 border border-gray-300`}>
                        {index + 1}
                    </td>
                    <td className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300`}>
                        <div>
                            <div className="font-normal">{language? item.sub_ledg_name: item?.sub_ledg_name_nep}</div>
                            {/* <div className="text-xs text-gray-500 dark:text-gray-400">{item.sub_ledg_name_nep}</div> */}
                        </div>
                    </td>
                    <td align='right' className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300`}>
                        {MoneyFormat(item.estimated_amount)}
                    </td>
                    <td align='right' className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300`}>
                        {MoneyFormat(item.real_amount)}
                    </td>
                    <td align='right' className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 ${diffClass}`}>
                        {MoneyFormat(item.difference_amount)}
                    </td>
                </tr>
            );
        });
    };

    return (
        <Fragment>
            <style>
                {`@media print {
            @page {
            size: A4 landscape;
            page-break-before: always;
            margin: 0.2in; 
            border: 1px solid oklch(0.145 0 0); 
         }
        }`}
            </style>
            <div className="bg-white h-screen">
                <div className="relative border-x border-t border-slate300 p-2">
                    <div className="relative">
                        <table className="w-full text-sm text-left rtl:text-right text-black">
                            <tbody>
                                <tr>
                                    <td
                                        className={`w-[25%] text-[12px] text-black whitespace-nowrap ${language ? "font-public-sans" : "font-sangrila"
                                            }`}
                                    >
                                        {language ? "Reg. No.: " : "दर्ता नं.:"}:{" "}
                                        {language
                                            ? Org_Details?.org_reg
                                            : NumberToNepaliNum(Org_Details?.org_reg || "")}
                                    </td>
                                    <td className="w-[25%]"></td>
                                    <td className="w-[25%]"></td>
                                    <td
                                        align="right"
                                        className={`w-[25%] text-[12px] text-black whitespace-nowrap ${language ? "font-public-sans" : "font-sangrila"
                                            }`}
                                    >
                                        {language ? "Phone: " : "फोन नम्बर:"}{" "}
                                        {language
                                            ? Org_Details?.org_phone
                                            : NumberToNepaliNum(Org_Details?.org_phone || 0)}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        rowSpan={4}
                                        className="w-[25%] font-medium text-black whitespace-nowrap"
                                    >
                                        <img
                                            width={60}
                                            height={60}
                                            src={AppURL.BaseURL + Org_Details?.logo}
                                            alt=""
                                        />
                                    </td>
                                    <td
                                        align="center"
                                        colSpan={2}
                                        className={`w-[25%] text-black font-semibold text-[18px] ${language ? "font-public-sans" : "font-sangrila"
                                            }`}
                                    >
                                        {language
                                            ? Org_Details?.org_name
                                            : Org_Details?.org_name_nep}
                                    </td>
                                    <td className="w-[25%]"></td>
                                </tr>

                                <tr>
                                    <td
                                        align="center"
                                        colSpan={2}
                                        className={`w-[25%] text-black font-normal text-[12px] ${language ? "font-public-sans" : "font-sangrila"
                                            }`}
                                    >
                                        {language ? Org_Details?.org_add : Org_Details?.org_add_nep}
                                    </td>
                                    <td
                                        align="right"
                                        className={`w-[25%] text-[11px] text-black font-normal ${language ? "font-public-sans" : "font-sangrila"
                                            }`}
                                    >
                                        {language ? "Date: " : "मिति: "}{" "}
                                        {menu_list?.np_date}
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        align="center"
                                        colSpan={2}
                                        className={`w-[25%] underline underline-3 text-black font-semibold text-[15px] ${language ? "font-public-sans" : "font-sangrila"
                                            }`}
                                    >
                                        {language ? "Income & Expenditure Comparative Report" : "बार्षिक बजेट र खर्च आम्दानीको तुलनात्मक रिपोर्ट"}
                                    </td>
                                    <td className="w-[25%]"></td>
                                </tr>
                                <tr>
                                    <td
                                        align="center"
                                        colSpan={2}
                                        className={`w-[25%] text-black font-light text-[11px] ${language ? "font-public-sans" : "font-sangrila"
                                            }`}
                                    >
                                        {language ? "Date: " : "मिति: "}
                                        {language
                                            ? account_rpt_from_to_date_list?.from_date
                                            : NumberToNepaliNum(
                                                account_rpt_from_to_date_list?.from_date || 0
                                            )}{" "}
                                        {language ? "from " : "बाट "}{" "}
                                        {language
                                            ? account_rpt_from_to_date_list?.to_date
                                            : NumberToNepaliNum(
                                                account_rpt_from_to_date_list?.to_date || 0
                                            )}{" "}
                                        {language ? "Upto " : "सम्म"}
                                    </td>
                                    <td align="right" className="w-[25%] text-[11px]"></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="relative">
                    <table className="w-full text-xs text-left text-gray-700 dark:text-gray-300">
                        <thead className="text-xs bg-gray-100 dark:bg-slate-800 sticky top-0">
                            <tr>
                                {/* Income Header */}
                                <th colSpan={5} className={`px-3 text-xs py-1 border border-gray-300  text-center bg-gray-50 ${language ? "font-public-sans" : "font-sangrila"}`}>
                                    <span>{language?"Income":"आम्दानी"}</span>
                                </th>

                                {/* Expense Header */}
                                <th colSpan={5} className={`px-3 text-xs py-1 border border-gray-300 text-center bg-gray-50 ${language ? "font-public-sans" : "font-sangrila"}`}>
                                    <span>{language?"Expense":"खर्च"}</span>
                                </th>
                            </tr>

                            {/* Sub Headers */}
                            <tr className="text-xs">
                                {/* Income Sub Headers */}
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-1 py-1 w-10 border border-gray-300 bg-gray-50`}>{language ? "S.N." : "क्र.सं"}</th>
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 bg-gray-50`}>{language ? "Ledger" : "शिर्षक"}</th>
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 bg-gray-50`}>{language ? "Estimated" : "अनुमानित रकम"}</th>
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 bg-gray-50`}>{language ? "Actual" : "बास्तवीक आम्दानी"}</th>
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border-r border-gray-300 bg-gray-50`}>{language ? "Difference" : "फरक"}</th>

                                {/* Expense Sub Headers */}
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-1 py-1 w-10 border border-gray-300 bg-gray-50`}>{language ? "S.N." : "क्र.सं"}</th>
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 bg-gray-50`}>{language ? "Ledger" : "शिर्षक"}</th>
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 bg-gray-50`}>{language ? "Estimated" : "अनुमानित रकम"}</th>
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 bg-gray-50`}>{language ? "Actual" : "बास्तवीक खर्च"}</th>
                                <th className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border-r border-gray-300 bg-gray-50`}>{language ? "Difference" : "फरक"}</th>
                            </tr>
                        </thead>

                        <tbody className="text-xs">
                            {/* Render income and expense rows side by side */}
                            {renderTableRows(incomeItems, true).map((incomeRow, index) => {
                                const expenseRow = renderTableRows(expenseItems, false)[index];
                                return (
                                    <React.Fragment key={index}>
                                        {React.cloneElement(incomeRow, {
                                            children: [
                                                ...incomeRow.props.children,
                                                ...expenseRow.props.children
                                            ]
                                        })}
                                    </React.Fragment>
                                );
                            })}

                            {/* Total Row */}
                            <tr className="font-bold bg-gray-100 dark:bg-slate-800">
                                {/* Income Totals */}
                                <td colSpan={2} className={`px-3 py-1 border border-gray-300 text-right ${language ? "font-public-sans" : "font-sangrila"}`}>
                                   {language?"Total Income:":"कुल आम्दानी:"} 
                                </td>
                                <td className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 text-right`}>
                                    {MoneyFormat(totalEstimates.income)}
                                </td>
                                <td className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 text-right`}>
                                    {MoneyFormat(totalActuals.income)}
                                </td>
                                <td className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border-r border-gray-300 text-right 
                                    text-gray-600 dark:text-gray-400
                                    `}>
                                    {MoneyFormat(totalDifferences.income)}
                                </td>

                                {/* Expense Totals */}
                                <td colSpan={2} className="px-3 py-1 border border-gray-300 text-right">
                                    {language?"Total Expense:":"कुल खर्च:"} 
                                </td>
                                <td className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 text-right`}>
                                    {MoneyFormat(totalEstimates.expense)}
                                </td>
                                <td className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 text-right`}>
                                    {MoneyFormat(totalActuals.expense)}
                                </td>
                                <td className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border border-gray-300 text-right text-gray-600'`}>
                                    {MoneyFormat(totalDifferences.expense)}
                                </td>
                            </tr>

                            {/* Net Profit/Loss Row */}
                            <tr className="font-bold dark:bg-blue-900/20">
                                <td colSpan={5} className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 border-r text-center`}>
                                    {language?"Net Result:":"कुल नतिजा:"} 
                                </td>
                                <td colSpan={5} className={`${language ? "font-public-sans" : "font-sangrila"} px-3 py-1 text-end text-xs text-gray-600`}>
                                    {MoneyFormat(totalActuals.income - totalActuals.expense)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Fragment>
    );
};

export default PrintYearlyBudgetReportView;
